import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import styles from './styles.module.scss';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import { SubjectsModel } from '@interfaces-clean/subject-monitoring.interface';
import { SubjectMonitoringContext } from '@pages-clean/central-monitoring/interfaces/context';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import { getSessionStorageSubjectCode, setSessionStorageSubjectCode } from '@contexts/local-state-context';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';

const GROUPED_BY_KEY = 'groupedBy';
const GROUPED_BY_VALUES = {
    ON_REVIEW: 'On Review',
    NOT_ON_REVIEW: 'Not On Review',
} as const;

interface Props {
    containerClassName?: string;
    className?: string;
    onChange?: (e: DropDownListChangeEvent) => unknown;
}

const SubjectSelector = ({ containerClassName, className, onChange }: Props) => {
    const { current, setCurrent } = useContext(SubjectMonitoringContext);

    const smActiveReviewContext = useContext(SMActiveReviewContext);
    const isInReviewMode = smActiveReviewContext?.state.reviewRecordId != null;

    // If in active review mode, get subjects on review. Otherwise, this does nothing
    const getReviewSubjectsQuery = SubjectReviewInstanceApi.useGetSubjects(
        current.studyRef,
        smActiveReviewContext?.state.reviewRecordId,
    );

    const useGetSubjectsQuery = SubjectMonitoringAPI.useGetSubjects(current.studyRef, current.dataset.id);

    // The last subject code that the user selected. If the dataset was changed, this may not refer to a subject in the current list
    // On mount, use the subject code from session storage (this gets cleared between datasets, which is why this ref exists...)
    const lastSelectedSubjectCodeRef = useRef(getSessionStorageSubjectCode());

    // Keeps selected subject data synced
    const setSelectedSubject = useCallback(
        (subject: SubjectsModel) => {
            setCurrent((old) => ({
                ...old,
                subjectId: subject.pgid,
                subjectCode: subject.code,
                subjectNumber: subject.subjectNumber,
            }));
            setSessionStorageSubjectCode(subject.code);
            lastSelectedSubjectCodeRef.current = subject.code;
        },
        [setCurrent],
    );

    const sortedSubjects = useMemo(() => {
        const subjectsAlphabetically = (useGetSubjectsQuery.data ?? []).toSorted((a, b) =>
            a.subjectNumber.localeCompare(b.subjectNumber, undefined, {
                numeric: false,
            }),
        );

        // If not in review mode, just return subjects alphabetically ordered
        if (!isInReviewMode) {
            return subjectsAlphabetically;
        }

        // If in review mode, add grouping property for if subject is on review or not, then sort "On Review" first
        const subjectsOnReviewIdSet = new Set(
            (getReviewSubjectsQuery.data ?? []).map((s) => s.subjectId).filter((x) => x != null),
        );

        return (
            subjectsAlphabetically
                // Add grouping property
                .map((s) => ({
                    ...s,
                    [GROUPED_BY_KEY]: subjectsOnReviewIdSet.has(s.pgid)
                        ? GROUPED_BY_VALUES.ON_REVIEW
                        : GROUPED_BY_VALUES.NOT_ON_REVIEW,
                }))
                // Sort "On Review" first
                .toSorted((a, b) => {
                    if (a[GROUPED_BY_KEY] === b[GROUPED_BY_KEY]) {
                        return 0;
                    }
                    return a[GROUPED_BY_KEY] === GROUPED_BY_VALUES.ON_REVIEW ? -1 : 1;
                })
        );
    }, [useGetSubjectsQuery.data, getReviewSubjectsQuery.data, isInReviewMode]);

    const selectedSubject = useMemo(() => {
        return useGetSubjectsQuery.data?.find((s) => s.code === current.subjectCode) ?? null;
    }, [useGetSubjectsQuery.data, current.subjectCode]);

    // On mount, and when subject list changes, if no subject is selected, select one.
    useEffect(() => {
        if (sortedSubjects.length === 0 || selectedSubject != null) {
            return;
        }

        // If they're in the list, re-select the last subject that the user selected
        const lastSelectedSubjectCode = lastSelectedSubjectCodeRef.current;
        const matchedSubject = sortedSubjects.find((s) => s.code === lastSelectedSubjectCode);
        // Otherwise, fallback to first subject in list
        const subject = matchedSubject ?? sortedSubjects[0];

        setSelectedSubject(subject);
    }, [setSelectedSubject, sortedSubjects, selectedSubject]);

    const isLoading = useGetSubjectsQuery.isLoading || getReviewSubjectsQuery.isLoading;

    return (
        <div className={containerClassName}>
            <BasicDropDown
                data-testid="subject-selector"
                data={sortedSubjects}
                dataItemKey="id"
                groupField={isInReviewMode ? GROUPED_BY_KEY : undefined}
                textField="subjectNumber"
                className={cn(styles.subjectSelector, className)}
                loading={isLoading}
                onChange={(e) => {
                    setSelectedSubject(e.value as SubjectsModel);
                    onChange && onChange(e);
                }}
                filterable
                value={selectedSubject}
            />
        </div>
    );
};

export default SubjectSelector;
